import React from 'react';

import { Chip, MenuItem } from 'components';

import { i18n, useTranslation } from 'third-party';

import { PayeeColors } from 'constants/common';

import { ListFilter } from 'components/Table/Filtering/ListFilter';
import { ColumnFilteringProps, TableColumn } from 'components/Table/types';

import { formatDateTimeWithTimezone } from 'utils/app';
import { getEnumValues } from 'utils/helper';

import { APIPayee, APIPayeeStatus } from 'types/api';

export const StatusMenu = getEnumValues(APIPayeeStatus)
  .filter(status => status !== APIPayeeStatus.NotActive)
  .map(status => (
    <MenuItem key={status} value={status}>
      {i18n.t(`payeeList.column.payeeStatus.${status}`).toString()}
    </MenuItem>
  ));

export const PayeeStatusFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      {getEnumValues(APIPayeeStatus)
        .filter(status => status !== APIPayeeStatus.NotActive)
        .map(status => (
          <MenuItem key={status} value={status}>
            {t(`payeeList.column.payeeStatus.${status}`)}
          </MenuItem>
        ))}
    </ListFilter>
  );
};

export const payeeColumns = (
  timezoneValue: string,
): TableColumn<APIPayee>[] => [
  {
    dataId: 'id',
    label: 'payeeList.column.id',
    minWidth: 80,
    width: 140,
    hidden: true,
    sortable: true,
    customFilter: PayeeStatusFilter,
    resizable: true,
  },
  {
    dataId: 'status',
    label: 'payeeList.column.status',
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    customFilter: PayeeStatusFilter,
    formatter: value => ({
      value: value,
      element: (
        <Chip
          label={i18n.t(`payeeList.column.payeeStatus.${value}`).toString()}
          style={{
            backgroundColor: PayeeColors[value as APIPayeeStatus],
          }}
        />
      ),
    }),
  },
  {
    dataId: 'nameOnCheck',
    label: 'payeeList.column.nameOnCheck',
    minWidth: 80,
    width: 140,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'email',
    label: 'payeeList.column.email',
    minWidth: 80,
    width: 140,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'phone',
    label: 'payeeList.column.phone',
    minWidth: 80,
    width: 100,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'address',
    label: 'payeeList.column.address',
    minWidth: 80,
    width: 170,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'city',
    label: 'payeeList.column.city',
    minWidth: 80,
    width: 140,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'state',
    label: 'payeeList.column.state',
    minWidth: 80,
    width: 80,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'zip',
    label: 'payeeList.column.zip',
    minWidth: 80,
    width: 100,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'user.firstName',
    label: 'payeeList.column.user.firstName',
    minWidth: 80,
    width: 100,
    hidden: true,
    sortable: true,
    resizable: true,
  },
  {
    dataId: 'user.lastName',
    label: 'payeeList.column.user.lastName',
    minWidth: 80,
    width: 100,
    hidden: true,
    sortable: true,
    resizable: true,
  },
  {
    dataId: 'user.email',
    label: 'payeeList.column.user.email',
    minWidth: 80,
    width: 100,
    hidden: true,
    sortable: true,
    resizable: true,
  },
  {
    dataId: 'createdOn',
    label: 'payeeList.column.createdOn',
    minWidth: 80,
    width: 180,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'date',
    formatter: value => ({
      value: formatDateTimeWithTimezone(value as string, timezoneValue),
    }),
  },
  {
    dataId: 'updatedOn',
    label: 'payeeList.column.updatedOn',
    minWidth: 80,
    width: 180,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'date',
    formatter: value => ({
      value: formatDateTimeWithTimezone(value as string, timezoneValue),
    }),
  },
];
